import Image from 'next/image'
import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import styles from './search-pills.module.scss'
import { ButtonHierarchy } from '@/components/styleguide/Button'
import { Carousel } from '@/components/styleguide/Carousel'
import Icon from '@/components/styleguide/Icon'
import { constants, FILTER_VEHICLE_TAGS } from '@/lib/constants'
import { useExperiment } from '@/lib/hooks/useExperiment'
import { addToDataLayer } from '@/lib/utilities/vehicle'
import { clearSelectedSearchFilter, storeSearchMultiParameters } from '@/store/reducers/search'
import { selectActiveVehicleTags, selectAvailableVehicleTags } from '@/store/selectors/search'
import { Size } from '@/types/system'

export const SearchPills = ({ hasPadding = true }) => {
  const [activePillsLength, setActivePillsLength] = useState(0)
  const startPoint = useRef(null)
  const [scrollValue, setScrollValue] = useState(null)
  const [experimentGroup] = useExperiment()
  const [isLoading, setIsLoading] = useState(false)
  const availableVehicleTags = useSelector(selectAvailableVehicleTags)
  const prevCount = usePrevious(availableVehicleTags)
  const activeVehicleTags = useSelector(selectActiveVehicleTags)
  const dispatch = useDispatch()

  useEffect(() => {
    const updateScrollValue = () => {
      if (startPoint.current) {
        setScrollValue(startPoint.current.offsetWidth)
      }
    }

    updateScrollValue()
    window.addEventListener('resize', updateScrollValue)

    return () => window.removeEventListener('resize', updateScrollValue)
  }, [startPoint])

  useEffect(() => {
    if (startPoint && startPoint.current) {
      setScrollValue(startPoint.current.offsetWidth)
    }
    const active = activeVehicleTags.length
    setActivePillsLength(active)
  }, [availableVehicleTags, activeVehicleTags, prevCount])

  function usePrevious(value) {
    const ref = useRef()
    useEffect(() => {
      ref.current = value
    })
    return ref.current
  }

  const onPillClick = (filter, value) => {
    setIsLoading(true)
    if (startPoint && startPoint.current) {
      startPoint.current.scrollLeft = 0
    }

    if (filter === FILTER_VEHICLE_TAGS) {
      addToDataLayer({
        event: 'search-pill',
        action: `${activeVehicleTags.includes(value) ? 'removed' : 'added'}`,
        label: availableVehicleTags.filter(tag => tag.id === value)[0].name,
        experimentGroup,
      })
    }

    dispatch(storeSearchMultiParameters({ filter, value }))
    setIsLoading(false)
  }

  const onClearPillsClick = () => {
    setIsLoading(true)
    if (startPoint && startPoint.current) {
      startPoint.current.scrollLeft = 0
    }
    addToDataLayer({
      event: 'search-pill',
      action: `clear-all`,
      experimentGroup,
    })
    dispatch(clearSelectedSearchFilter(FILTER_VEHICLE_TAGS))
    setIsLoading(false)
  }

  if (availableVehicleTags.length === 0) {
    return false
  }

  const getPills = () => {
    const pills = availableVehicleTags

    if (pills.length === 0) {
      return []
    }

    return [...pills]
      .sort((a, b) => [5, 6, 7, 10, 11, 12].includes(b.id) - [5, 6, 7, 10, 11, 12].includes(a.id))
      .map(tag => ({
        chipId: tag.id,
        chipType: ButtonHierarchy.ChipFilter,
        label: tag.name,
        disabled: isLoading,
      }))
  }

  const renderPills = items => {
    const PillComponent = pill => {
      const isSelected = activeVehicleTags.includes(pill.chipId)

      return (
        <button
          onClick={() => onPillClick(FILTER_VEHICLE_TAGS, pill.chipId)}
          type="button"
          key={pill.chipId}
          data-chip-id={pill.chipId}
          className={styles.chip}
          data-is-selected={isSelected}>
          <span className={styles['image-wrapper']}>
            <span className={styles.rect}></span>
            <span
              className={`${styles.image} ${[5, 6, 7].includes(pill.chipId) ? styles.bespoke : ''}`}>
              {[5, 6, 7].includes(pill.chipId) ? (
                <Image
                  className={styles['bespoke-chip']}
                  alt={pill.label}
                  src={`${constants.imageHost.assets}/vehicle-tag-vehicles/${pill.chipId}.svg`}
                  width={504}
                  height={336}
                />
              ) : (
                <Image
                  alt={`${pill.label} vehicle`}
                  src={`${constants.imageHost.assets}/vehicle-tag-vehicles/${pill.chipId}.png`}
                  width={504}
                  height={336}
                />
              )}
            </span>
            {isSelected && (
              <span className={styles.check}>
                <Icon
                  name="Check"
                  size={Size.ExtraSmall}
                />
              </span>
            )}
          </span>
          <span className={styles.label}>{pill.label}</span>
        </button>
      )
    }

    return (
      <>
        {items
          .filter(
            i => activeVehicleTags.includes(i.chipId) || [5, 6, 7, 10, 11, 12].includes(i.chipId),
          )
          .sort(
            (a, b) => activeVehicleTags.includes(b.chipId) - activeVehicleTags.includes(a.chipId),
          )
          .map(PillComponent)}
        {items
          .filter(
            i => !activeVehicleTags.includes(i.chipId) && ![5, 6, 7, 10, 11, 12].includes(i.chipId),
          )
          .sort((a, b) => a.chipId - b.chipId)
          .map(PillComponent)}
      </>
    )
  }

  return (
    <Carousel
      scrollValue={scrollValue}
      sectionName="search-pills"
      triggerScrollUpdate={setIsLoading}>
      <div
        className={styles['pill-wrapper']}
        data-has-padding={hasPadding}
        ref={startPoint}>
        {renderPills(getPills())}
        {activePillsLength > 0 && (
          <button
            type="button"
            className={styles['text-link']}
            onClick={onClearPillsClick}>
            Clear all
          </button>
        )}
      </div>
    </Carousel>
  )
}
